import { h, Host, Listen, Element, Component, Prop, Watch, State, Event, EventEmitter } from '@stencil/core';
import state from './state';
import { addMobileClass } from './utils';
import { createFocusTrap, FocusTrap } from '../../utils/focus-trap';

import type { NotificationContent, NotificationMode, Layout, NotificationSubmitEventArg } from './types';
import type { HighlightableElement } from '../acquisition-overlay/components/clickable-element';

@Component({
  tag: 'notification-overlay',
  styleUrl: './notification-overlay.css',
  shadow: true,
})
export class NotificationOverlay {
  anchorElements: HTMLAnchorElement[] = [];
  private focusTrap: FocusTrap | null = null;

  @Element() el: HTMLElement;

  @Prop() values: string | (NotificationContent & { layout?: Layout }) = '';
  @Prop() forcemobileview: boolean = false;
  @Prop() mode: NotificationMode = 'live';
  @Prop() account: string;
  @Prop() highlightedClickableElement: HighlightableElement | null = null;
  @Prop() preventHighlight = false;
  @Prop() overlayId: string;
  @Prop() layout: Layout = { name: 'default' };

  @State() numImagesToLoad = 0;

  @Listen('resize', { target: 'window' })
  handleResize() {
    this.checkForMobileViewport(window.innerWidth);
  }

  @Listen('keyup', { target: 'window' })
  handleEscapeKey(e: KeyboardEvent) {
    this.onEscapeKey(e);
  }

  @Listen('om:notification_close')
  handleOverlayClose() {
    this.deactivateFocusTrap();
  }

  @Event({
    eventName: 'om:notification_cta_click',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  ctaClickEvent: EventEmitter<NotificationSubmitEventArg>;

  actionEventHandler(data: NotificationSubmitEventArg) {
    if (state.mode !== 'live') {
      return;
    }
    this.ctaClickEvent?.emit(data);
  }

  @Event({
    eventName: 'om:notification_close',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  closeEvent: EventEmitter;

  closeEventHandler() {
    if (this.mode === 'preview') {
      return;
    }

    this.closeEvent?.emit();
  }

  @Watch('values')
  parseValues(newValues: string | (NotificationContent & { layout?: Layout })) {
    let data: NotificationContent & { layout?: Layout };

    if (typeof newValues === 'string') {
      const decodedValues = newValues ? decodeURIComponent(newValues) : null;
      if (decodedValues) {
        data = JSON.parse(decodedValues);
      }
    } else {
      data = newValues;
    }
    const { layout, ...content } = data;
    state.layout = layout ?? {};
    state.content = content as NotificationContent;
  }

  @Watch('highlightedClickableElement')
  handlePropChangeHighlightedClickableElement(newValue: HighlightableElement) {
    state.highlightElement = newValue;
  }

  checkForMobileViewport(windowWidth: number) {
    if (this.forcemobileview) return;
    state.isMobileViewport = windowWidth <= 568;
  }

  handleBackgroundClick(event: Event) {
    if (state.mode === 'preview') return;

    const target = event.target as HTMLElement;
    if (target.classList.contains('full-screen') || target.classList.contains('overlayInner-container')) {
      this.unmount();
    }
  }

  unmount() {
    this.deactivateFocusTrap();
    document.getElementsByTagName('notification-overlay')[0]?.remove();
    this.closeEventHandler();
  }

  private initializeFocusTrap() {
    if (!this.el.shadowRoot) return;

    this.focusTrap = createFocusTrap({
      container: this.el.shadowRoot,
      onEscape: () => {
        if (this.mode !== 'preview') {
          this.unmount();
        }
      },
      trapFocus: this.mode !== 'preview',
    });
  }

  private activateFocusTrap() {
    if (this.focusTrap) {
      this.focusTrap.activate();
    }
  }

  private deactivateFocusTrap() {
    if (this.focusTrap) {
      this.focusTrap.deactivate();
      this.focusTrap = null;
    }
  }

  private template() {
    const { name: layoutName, ...layoutProps } = state.layout;

    return (
      <Host>
        <div
          id="om_notification_backdrop"
          class={`
          overlay-container
          ${this.preventHighlight ? 'no-pointer' : ''}
          ${this.mode === 'preview' ? 'preview-screen' : 'full-screen'}
          ${this.forcemobileview || state.isMobileViewport ? 'is-mobile-viewport' : ''}
        `}
          onClick={e => {
            this.handleBackgroundClick(e);
          }}
        >
          {(() => {
            const commonProps = {
              unmount: this.unmount.bind(this),
              handleAction: this.actionEventHandler.bind(this),
            };

            switch (layoutName) {
              case 'default':
                return <om-default-layout {...commonProps} {...layoutProps} />;
              case 'split':
                return <om-split-layout {...commonProps} {...layoutProps} />;
              case 'positioned':
                return <om-positioned-layout {...commonProps} {...layoutProps} />;
              default:
                // Fallback to simplified layout structure for notifications
                let containerClass = addMobileClass('container');
                if ('maxSize' in layoutProps && layoutProps.maxSize) containerClass += ' max-size';

                return (
                  <div class={containerClass} role="dialog" aria-modal="true" tabindex={-1}>
                    <om-notification-bg-images />
                    <om-notification-close-button handleClose={this.unmount.bind(this)} />
                    <om-notification-screen-switcher handleAction={this.actionEventHandler.bind(this)} />
                  </div>
                );
            }
          })()}
        </div>
      </Host>
    );
  }

  componentWillLoad() {
    try {
      state.mode = this.mode;
      state.preventHighlight = this.preventHighlight;
      state.account = this.account;
      state.overlayId = this.overlayId;
      state.shadowRoot = this.el.shadowRoot;
      state.isMobileViewport = this.forcemobileview;

      this.parseValues(this.values);

      if (!state.content) {
        return;
      }

      // Preload fonts (reusing the same pattern as acquisition overlay)
      const head = document.head;
      const linkGstatic = document.createElement('link');
      linkGstatic.href = 'https://fonts.gstatic.com';
      linkGstatic.rel = 'preconnect';
      linkGstatic.crossOrigin = '';
      head.appendChild(linkGstatic);

      const linkGoogleapis = document.createElement('link');
      linkGoogleapis.href = 'https://fonts.googleapis.com';
      linkGoogleapis.rel = 'preconnect';
      head.appendChild(linkGoogleapis);

      const link = document.createElement('link');
      link.href =
        'https://fonts.googleapis.com/css2?family=GFS+Didot&family=Lato&family=Open+Sans&family=Roboto&family=Libre+Baskerville&family=Montserrat&display=swap';
      link.rel = 'stylesheet';
      head.appendChild(link);

      // Preload background images from all screens
      const addLinkToHead = (url: string, rel: 'prefetch' | 'preload') => {
        const link = document.createElement('link');
        link.rel = rel;
        link.as = 'image';
        link.href = url;

        if (rel === 'preload') {
          link.fetchPriority = 'high';
          this.numImagesToLoad = this.numImagesToLoad + 1;
          link.addEventListener('load', () => {
            this.numImagesToLoad = this.numImagesToLoad - 1;
          });
        }

        head.appendChild(link);
      };

      // Preload background images
      const { background } = state.content ?? {};
      if (background) {
        const { computer, mobile } = background;

        if (computer?.imageUrl) {
          addLinkToHead(computer.imageUrl, 'preload');
        }

        if (mobile?.imageUrl) {
          addLinkToHead(mobile.imageUrl, 'preload');
        }
      }
    } catch (error) {
      console.error('Error parsing notification overlay values', error);
      return null;
    }
  }

  onEscapeKey(e: KeyboardEvent) {
    if (e.key !== 'Escape') return;

    if (this.mode !== 'preview') {
      this.unmount();
    }
  }

  componentDidRender() {
    this.preventAnchorPropagation();
    this.checkForMobileViewport(window.innerWidth);
    this.handlePropChangeHighlightedClickableElement(this.highlightedClickableElement);

    if (!this.focusTrap) {
      this.initializeFocusTrap();
    }
    this.activateFocusTrap();
  }

  disconnectedCallback() {
    this.deactivateFocusTrap();
  }

  preventAnchorPropagation() {
    if (this.mode !== 'preview') return;
    const eventListener = [
      'click',
      (e: MouseEvent) => {
        e.stopPropagation();
      },
    ] as const;

    while (this.anchorElements.length) {
      this.anchorElements.pop().removeEventListener(...eventListener);
    }

    this.el.shadowRoot.querySelectorAll('a').forEach(el => {
      el.addEventListener(...eventListener);
      this.anchorElements.push(el);
    });
  }

  render() {
    if (this.numImagesToLoad > 0) return;
    return this.template();
  }
}
